import clsx from 'clsx';
import { monthLabel } from '../lib/incident-helpers';
import type { IncidentsResponse } from '../lib/incidents-api';
import { Frame } from './Frame';
import { IncidentRow } from './IncidentRow';
import { SectionHeading } from './SectionHeading';

interface Props {
  data: IncidentsResponse | undefined;
  loading: boolean;
  error: Error | null;
  onSelect: (id: number) => void;
}

export function IncidentsList({ data, loading, error, onSelect }: Props) {
  return (
    <section aria-labelledby="incidents-title">
      <Frame className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4 px-4 pb-7 pt-[88px] min-[768px]:px-[42px] min-[768px]:pb-8 min-[768px]:pt-[120px]">
        <SectionHeading
          id="incidents-title"
          label="Incidents"
          title="Past incidents"
        />
        {data ? (
          <span className="text-[14px] leading-[1.4] text-ink-3 [font-feature-settings:'tnum']">
            Last {data.days} days
          </span>
        ) : null}
      </Frame>

      <Frame lines="both" className="bg-paper-1">
        {loading ? (
          <div className="px-4 py-6 min-[768px]:px-5" aria-busy="true">
            <div className="skeleton-shine h-4 w-48 rounded" />
          </div>
        ) : error ? (
          <div className="px-4 py-6 text-[14px] text-down-fg min-[768px]:px-5">
            Failed to load incidents.
          </div>
        ) : !data || data.incidents.length === 0 ? (
          <div className="px-4 py-6 text-[14px] text-ink-3 min-[768px]:px-5">
            No incidents reported.
          </div>
        ) : (
          data.groupedByMonth.map((group, groupIdx) => (
            <div
              key={group.month}
              className={clsx(groupIdx > 0 && 'border-t border-line')}
            >
              <div className="bg-paper-3 px-4 py-3 text-[14px] font-medium leading-none text-ink-3 min-[768px]:px-5">
                {monthLabel(group.month)}
              </div>
              {group.incidents.map((incident) => (
                <IncidentRow
                  key={incident.id}
                  incident={incident}
                  onClick={onSelect}
                />
              ))}
            </div>
          ))
        )}
      </Frame>
    </section>
  );
}
